import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Ban, Scale, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <Helmet>
        <title>Terms of Service | SharePay India</title>
        <meta name="description" content="SharePay India Terms of Service - Read our terms and conditions for using the platform." />
        <link rel="canonical" href="https://sharepay.today/terms" />
      </Helmet>

      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-bold gradient-text">Terms of Service</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
            <p className="text-muted-foreground">Last Updated: January 30, 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-foreground">
            {/* Introduction */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to SharePay. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services. SharePay is a peer-to-peer (P2P) group payment utility platform based in India that supports rent splitting, trip budgeting, birthday contributions, bill reminders, and collaborative expense management.
              </p>
            </section>

            {/* Eligibility */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-500" /> 2. Eligibility
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To use SharePay, you must:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li>Be at least <strong>18 years of age</strong></li>
                <li>Be a <strong>resident of India</strong></li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Have a valid UPI-enabled bank account</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                By using SharePay, you represent and warrant that you meet all eligibility requirements.
              </p>
            </section>

            {/* Account Registration */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">3. Account Registration & Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Immediately notifying us of any unauthorized access</li>
                <li>Ensuring your contact information is up-to-date</li>
              </ul>
            </section>

            {/* UPI & Payments */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-cyan-500" /> 4. UPI Integration & Payments
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                SharePay facilitates payments through UPI (Unified Payments Interface) and may integrate with third-party payment rails such as Razorpay.
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li><strong>User Authorization:</strong> All UPI transactions are user-authorized. You must approve each payment through your UPI app.</li>
                <li><strong>Transaction Verification:</strong> Users are solely responsible for verifying transaction details (amount, recipient, UPI ID) before confirming any payment.</li>
                <li><strong>In-App Wallet:</strong> If offered, any in-app wallet functionality is subject to RBI guidelines and additional terms.</li>
                <li><strong>Payment Processing:</strong> Payments are processed through secure, RBI-compliant payment gateways.</li>
              </ul>
            </section>

            {/* Group Features */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">5. Groups & Payment Reminders</h2>
              <p className="text-muted-foreground leading-relaxed">
                SharePay allows users to create and join groups for shared expenses. Key terms:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li><strong>Group Admins:</strong> Group creators/admins can send payment reminders to members</li>
                <li><strong>Reminders:</strong> Automated and manual reminders may be sent for pending payments</li>
                <li><strong>Pay for Me:</strong> Members can cover other members' shares with agreed repayment terms</li>
                <li><strong>Settlement:</strong> Users can settle balances directly through UPI</li>
              </ul>
            </section>

            {/* Liability */}
            <section className="rounded-2xl bg-card border border-border p-6 border-l-4 border-l-orange-500">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" /> 6. Limitation of Liability
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <strong>IMPORTANT:</strong> SharePay is a platform that facilitates expense tracking and payment coordination. We are NOT a bank, payment service provider, or financial institution.
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li><strong>No Liability for Non-Payment:</strong> SharePay holds no liability for non-payment, delayed payment, or disputes between users regarding shared expenses.</li>
                <li><strong>User Disputes:</strong> Any disputes between group members must be resolved directly between the parties involved.</li>
                <li><strong>Transaction Errors:</strong> SharePay is not responsible for errors in transactions initiated by users through UPI or other payment methods.</li>
                <li><strong>No Guarantee:</strong> We do not guarantee that any user will fulfill their payment obligations.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHAREPAY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM.
              </p>
            </section>

            {/* Prohibited Activities */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" /> 7. Prohibited Activities
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree NOT to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li>Use the platform for any illegal purpose or money laundering</li>
                <li>Create fake accounts or impersonate others</li>
                <li>Harass other users or send spam</li>
                <li>Attempt to hack, reverse-engineer, or disrupt our services</li>
                <li>Use the platform for commercial purposes without authorization</li>
                <li>Violate any applicable Indian laws or regulations</li>
              </ul>
            </section>

            {/* Account Suspension */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">8. Account Suspension & Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                SharePay reserves the right to suspend or terminate your account immediately, without prior notice, if:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li>You violate these Terms of Service</li>
                <li>We detect fraudulent or suspicious activity</li>
                <li>You misuse the platform or harm other users</li>
                <li>Required by law enforcement or regulatory authorities</li>
                <li>Your account remains inactive for an extended period</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Upon termination, your right to use the platform will immediately cease. Any outstanding balances or disputes must be resolved independently.
              </p>
            </section>

            {/* Termination by User */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">9. Termination by User</h2>
              <p className="text-muted-foreground leading-relaxed">
                You may terminate your account at any time through the app settings or by contacting support. Upon account deletion:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li>All your personal data will be permanently deleted within 30 days</li>
                <li>Outstanding balances with other users must be settled beforehand</li>
                <li>You will lose access to all groups and transaction history</li>
                <li>This action is irreversible</li>
              </ul>
            </section>

            {/* Governing Law */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-cyan-500" /> 10. Governing Law & Disputes
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of SharePay shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka, India.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Before initiating legal proceedings, you agree to attempt resolution through our support team at <a href="mailto:support@sharepay.today" className="text-cyan-500 hover:underline">support@sharepay.today</a>.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">11. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update these Terms from time to time. We will notify you of any material changes via email or in-app notification at least 15 days before the changes take effect. Your continued use of SharePay after the effective date constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 p-6 text-white">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" /> 12. Contact Information
              </h2>
              <p className="leading-relaxed opacity-90">
                For any questions, concerns, or disputes regarding these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-white/10 rounded-xl">
                <p className="font-medium">SharePay Legal Team</p>
                <p className="opacity-90">Email: <a href="mailto:support@sharepay.today" className="underline">support@sharepay.today</a></p>
                <p className="opacity-90 mt-2">We aim to respond within 48 business hours.</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="rounded-2xl bg-muted p-6 text-center">
              <p className="text-muted-foreground">
                By using SharePay, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
